import { api } from '../context/AppContext';

// VAPID public key from environment — no fallback, must be configured
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// Register service worker
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return { granted: false, reason: 'unsupported' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { 
      granted: permission === 'granted',
      permission 
    };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { granted: false, reason: 'error', error };
  }
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }

  const registration = await navigator.serviceWorker.ready;
  
  // Check for existing subscription
  let subscription = await registration.pushManager.getSubscription();
  
  if (!subscription) {
    // Create new subscription
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
  }

  // Send subscription to backend
  const subscriptionData = subscription.toJSON();
  await api.post('/notifications/subscribe', {
    endpoint: subscriptionData.endpoint,
    keys: subscriptionData.keys
  });

  return subscription;
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    await subscription.unsubscribe();
  }

  // Notify backend
  await api.delete('/notifications/unsubscribe');
}

// Check if currently subscribed
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

// Get notification settings
export async function getNotificationSettings() {
  try {
    const response = await api.get('/notifications/settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching notification settings:', error);
    return {
      enabled: true,
      breakfast_time: '08:00',
      lunch_time: '12:00',
      dinner_time: '19:00',
      weekly_summary: true
    };
  }
}

// Update notification settings
export async function updateNotificationSettings(settings) {
  const response = await api.put('/notifications/settings', settings);
  return response.data;
}

// Show local notification (for testing)
export function showLocalNotification(title, body, options = {}) {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    console.warn('Cannot show notification');
    return;
  }

  navigator.serviceWorker.ready.then((registration) => {
    registration.showNotification(title, {
      body,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-96x96.png',
      tag: 'local-notification',
      ...options
    });
  });
}

// Schedule meal reminders (client-side fallback using setTimeout)
// Note: For production, use backend cron jobs with web-push library
export function scheduleMealReminders(settings) {
  if (!settings.enabled) return;

  const scheduleReminder = (time, mealType) => {
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    const reminderTime = new Date();
    reminderTime.setHours(hours, minutes, 0, 0);

    // If time has passed today, schedule for tomorrow
    if (reminderTime <= now) {
      reminderTime.setDate(reminderTime.getDate() + 1);
    }

    const delay = reminderTime.getTime() - now.getTime();

    setTimeout(() => {
      const messages = {
        breakfast: {
          fr: 'Bon matin! N\'oublie pas de photographier ton déjeuner! 🍳',
          en: 'Good morning! Don\'t forget to photograph your breakfast! 🍳'
        },
        lunch: {
          fr: 'C\'est l\'heure du dîner! Photographie ton repas! 🥗',
          en: 'Lunch time! Photograph your meal! 🥗'
        },
        dinner: {
          fr: 'Bon appétit! N\'oublie pas ta photo de souper! 🍽️',
          en: 'Bon appetit! Don\'t forget your dinner photo! 🍽️'
        }
      };

      const lang = localStorage.getItem('language') || 'fr';
      showLocalNotification('EXTRA MILES', messages[mealType][lang]);

      // Reschedule for next day
      scheduleMealReminders(settings);
    }, delay);
  };

  scheduleReminder(settings.breakfast_time, 'breakfast');
  scheduleReminder(settings.lunch_time, 'lunch');
  scheduleReminder(settings.dinner_time, 'dinner');
}
